import { useState } from "react";
import { Check } from "lucide-react";
import { CONTACT } from "@/lib/contact";
import { scrollToId } from "@/lib/scroll";
import FormNotice from "@/components/FormNotice";

const inputCls =
    "w-full rounded-xl border border-black/10 bg-[#f6f7fb] px-4 py-3 text-sm text-[#0a0a0b] outline-none focus:border-[var(--brand)]/60 focus:ring-2 focus:ring-[var(--brand)]/20";

const included = [
    "Custom one-page website",
    "Mobile-friendly layout",
    "Your logo, colours, and photos",
    "Services, hours, and location",
    "Click-to-call and social links",
    "Contact form, when you need one",
    "Google Maps link",
    "Basic search-friendly structure",
    "One round of revisions",
    "Help going live",
];

const steps = [
    {
        title: "Send the details",
        copy: "Business name, what you do, phone, address, logo, and photos. If you already have a site, send that too.",
    },
    {
        title: "Pay $199",
        copy: "The build starts after the $199 CAD is in. Domain fees, if you need one, are separate.",
    },
    {
        title: "We build it",
        copy: "Typical turnaround is 24–48 hours once we have everything. You get one round of reasonable changes.",
    },
    {
        title: "Go live",
        copy: "We connect your domain, or help you buy one, and put the page online.",
    },
];

const upgrades = [
    { title: "Extra pages", price: "from $49/page", copy: "Services, about, gallery, FAQ, or contact as their own pages." },
    { title: "Booking", price: "from $49", copy: "A link into the scheduler you already use." },
    { title: "Google Business Profile", price: "from $49", copy: "The listing customers see in search, cleaned up." },
    { title: "Website care", price: "from $29/month", copy: "Updates after launch, month to month." },
    { title: "A larger build", price: "Quoted", copy: "Anything past a one-page site is a separate quote." },
];

const faqs = [
    {
        q: "Is it really $199?",
        a: "Yes. The one-page site is $199 CAD. Extra pages, a domain, and anything outside this list are separate.",
    },
    {
        q: "How fast is it?",
        a: "24–48 hours after we have the information and the payment.",
    },
    {
        q: "Do I need a domain?",
        a: "If you have one, we connect it. If you don't, we can help you buy one. Registration is not included in the $199.",
    },
    {
        q: "Can I change it?",
        a: "One revision round is included. After that, changes are quoted or covered by website care.",
    },
    {
        q: "What if I need more than one page?",
        a: "Start with the one-page site, then add pages, or ask for a quote on a larger build.",
    },
];

function Cta({ children, testId, className = "" }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={() => scrollToId("#get-started")}
            className={`inline-flex items-center justify-center rounded-full bg-[var(--brand)] px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(43,80,255,0.35)] transition-transform hover:-translate-y-0.5 ${className}`}
        >
            {children}
        </button>
    );
}

export default function WebsitesOfferPage() {
    const [submitted, setSubmitted] = useState(false);

    const onSubmit = (event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const business = data.get("business");
        const body = [
            `Name: ${data.get("name")}`,
            `Email: ${data.get("email")}`,
            `Business: ${business}`,
            `Phone: ${data.get("phone")}`,
            "Offer: $199 CAD one-page website",
        ].join("\n");
        const subject = encodeURIComponent(`$199 website — ${business}`);
        window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${encodeURIComponent(body)}`;
        setSubmitted(true);
    };

    return (
        <main data-testid="websites-offer-page" className="bg-white text-[#0a0a0b]">
            <section className="bg-lavender px-5 pb-16 pt-36 sm:pb-20 sm:pt-44">
                <div className="mx-auto grid max-w-5xl items-start gap-10 lg:grid-cols-[1.1fr_0.9fr]">
                    <div>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--brand)] ring-1 ring-black/5">
                            $199 CAD
                        </span>
                        <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl">
                            A one-page website for <span className="text-gradient">$199.</span>
                        </h1>
                        <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[#5b5b66] sm:text-lg">
                            A clean page for your business, on phones and computers. One price. No contract. You own the site.
                        </p>
                        <ul className="mt-6 space-y-2 text-sm text-[#3a3a44]">
                            {["One page", "One revision", "24–48 hours once we have your details"].map((item) => (
                                <li key={item} className="flex items-center gap-2">
                                    <Check className="h-4 w-4 text-[var(--brand)]" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div
                        id="get-started"
                        data-testid="websites-intake-section"
                        className="rounded-3xl bg-white p-6 shadow-[0_20px_55px_rgba(15,23,42,0.10)] ring-1 ring-black/5 sm:p-7"
                    >
                        {submitted ? (
                            <div data-testid="websites-form-success" className="py-10 text-center">
                                <h2 className="font-display text-2xl font-extrabold">Request ready to send</h2>
                                <p className="mt-3 text-sm leading-relaxed text-[#5b5b66]">
                                    Your email app should be open to {CONTACT.email}. Send it and we reply with payment
                                    and what to send us. The build starts after the $199 is in.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={onSubmit} className="space-y-4">
                                <div>
                                    <h2 className="font-display text-2xl font-extrabold">Get the $199 website</h2>
                                    <p className="mt-1 text-sm text-[#5b5b66]">Tell us who you are. We reply from St. Catharines.</p>
                                </div>
                                <div>
                                    <label htmlFor="website-name" className="mb-1.5 block text-sm font-medium text-[#5b5b66]">
                                        Your name
                                    </label>
                                    <input id="website-name" name="name" data-testid="websites-form-name-input" required placeholder="Jane Tremblay" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="website-email" className="mb-1.5 block text-sm font-medium text-[#5b5b66]">
                                        Email
                                    </label>
                                    <input id="website-email" name="email" type="email" data-testid="websites-form-email-input" required placeholder="jane@business.ca" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="website-business" className="mb-1.5 block text-sm font-medium text-[#5b5b66]">
                                        Business name
                                    </label>
                                    <input id="website-business" name="business" data-testid="websites-form-business-input" required placeholder="Northline Plumbing" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="website-phone" className="mb-1.5 block text-sm font-medium text-[#5b5b66]">
                                        Phone
                                    </label>
                                    <input id="website-phone" name="phone" type="tel" data-testid="websites-form-phone-input" required placeholder="Your phone" className={inputCls} />
                                </div>
                                <button
                                    type="submit"
                                    data-testid="websites-form-submit-button"
                                    className="flex w-full items-center justify-center rounded-full bg-[var(--brand)] px-6 py-3.5 text-base font-semibold text-white shadow-[0_12px_28px_rgba(43,80,255,0.38)] transition-transform hover:-translate-y-0.5"
                                >
                                    Get my $199 website
                                </button>
                                <p className="text-center text-xs text-[#8a8a95]">$199 CAD before the build starts.</p>
                                <FormNotice className="text-center" />
                            </form>
                        )}
                    </div>
                </div>
            </section>

            <section className="px-5 py-16 sm:py-20">
                <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
                    <div>
                        <h2 className="font-display text-3xl font-extrabold sm:text-4xl">Included for $199</h2>
                        <p className="mt-3 text-sm leading-relaxed text-[#5b5b66]">
                            Forms say why they collect information. Marketing email is opt-in. Before launch we check
                            contrast, heading order, and keyboard access. That is a build standard, not a legal opinion.
                        </p>
                        <Cta testId="websites-included-cta" className="mt-6">
                            Get my $199 website
                        </Cta>
                    </div>
                    <ul className="grid gap-3 sm:grid-cols-2">
                        {included.map((item) => (
                            <li key={item} className="flex items-start gap-2 rounded-2xl bg-[#f6f7fb] px-4 py-3 text-sm text-[#3a3a44]">
                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--brand)]" />
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="bg-[#f6f7fb] px-5 py-16 sm:py-20">
                <div className="mx-auto max-w-5xl">
                    <h2 className="font-display text-3xl font-extrabold">How it works</h2>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {steps.map((step, index) => (
                            <article key={step.title} className="rounded-3xl bg-white p-5 ring-1 ring-black/5">
                                <p className="text-sm font-extrabold text-[var(--brand)]">{String(index + 1).padStart(2, "0")}</p>
                                <h3 className="mt-3 font-display text-lg font-bold">{step.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-[#5b5b66]">{step.copy}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-5 py-16 sm:py-20">
                <div className="mx-auto max-w-5xl">
                    <h2 className="font-display text-3xl font-extrabold">If you need more</h2>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#5b5b66]">
                        The $199 site can grow. You only pay for the extra piece.
                    </p>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {upgrades.map((item) => (
                            <article key={item.title} className="rounded-3xl bg-[#f6f7fb] p-5 ring-1 ring-black/5">
                                <p className="text-sm font-semibold text-[var(--brand)]">{item.price}</p>
                                <h3 className="mt-2 font-display text-lg font-bold">{item.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-[#5b5b66]">{item.copy}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#f6f7fb] px-5 py-16 sm:py-20">
                <div className="mx-auto max-w-3xl">
                    <h2 className="font-display text-3xl font-extrabold">Questions</h2>
                    <div className="mt-6 space-y-3">
                        {faqs.map((item) => (
                            <details key={item.q} className="rounded-2xl bg-white p-5 ring-1 ring-black/5">
                                <summary className="cursor-pointer font-semibold">{item.q}</summary>
                                <p className="mt-2 text-sm leading-relaxed text-[#5b5b66]">{item.a}</p>
                            </details>
                        ))}
                    </div>
                    <div className="mt-10 text-center">
                        <Cta testId="websites-hero-cta">Get my $199 website</Cta>
                    </div>
                </div>
            </section>
        </main>
    );
}
